// @ts-nocheck
import React, { useCallback, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Tooltip,
} from '@mui/material';
import PropTypes from 'prop-types';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import useDocumentInventory from '../../../../hooks/ModelInventory/useDocumentInventory';
import { useDialogFullscreen } from '../../../../hooks/useDialogFullscreen';

// Import specific field components for modal data types
import {
  ChecklistFieldComponent,
  DocumentFieldComponent,
  TableFieldComponent,
  IframeFieldComponent,
  TextFieldExtendedComponent,
  AdvancedTableRenderer,
  ParagraphFieldRenderer,
} from '../FieldComponents';
import {
  checkAttributeIsRequired,
  isValueEmpty,
} from '../../../../utils/Utils';
import { useUploadDocumentContext } from '../../../../context/DocumentUpload/UploadDocumentContext';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../../../const/CommonConst';

/**
 * Modal component for editing complex attribute values in multi-edit mode
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.handleClose - Function to close the modal
 * @param {Object} props.attribute - The attribute being edited
 * @param {any} props.value - The current value of the attribute
 * @param {Function} props.handleSave - Function to save the new value
 * @param {Array} props.groupList - List of available groups (for Group type)
 * @param {Array} props.associationList - List of available associations (for Association type)
 * @param {Object} props.entityData - Entity data for document fields
 * @returns {React.ReactElement} The MultiEditModal component
 */
const MultiEditModal = ({
  open,
  handleClose,
  attribute,
  value,
  handleSave,
  entityData = EMPTY_OBJECT,
  entityDetailsFormikForm,
  handleAutoSave,
  isDerivedExtended = 'false',
  usersList = EMPTY_ARRAY,
  groupList = EMPTY_ARRAY,
  configColumn = false,
  modelTemplateDetails = EMPTY_OBJECT,
}) => {
  const initialValueRef = useRef(null);
  const { deleteDocument } = useDocumentInventory();
  const { handleStopUpload, activeBatches } = useUploadDocumentContext();
  const { isFullScreen, toggleFullScreen, exitFullScreen } =
    useDialogFullscreen();

  const isRequired = useMemo(
    () => checkAttributeIsRequired(attribute),
    [attribute]
  );
  /**
   * Handles value change from field components
   * @function
   * @param {any} newValue - The new value
   * @returns {void}
   */
  const handleValueChange = useCallback(
    (newValue) => {
      const isDocType = attribute?.data_type === 'Document';
      // For document attributes, we only update Formik/Draft state if:
      // 1. the value is being cleared (to properly reflect emptiness)
      // 2. OR it already contains a document_id (meaning it was fully uploaded)
      const shouldUpdateState =
        !isDocType ||
        isValueEmpty(newValue) ||
        (Array.isArray(newValue) &&
          newValue?.length > 0 &&
          newValue[0]?.document_id);

      if (shouldUpdateState) {
        handleSave(newValue);
      }

      if (attribute?.id && handleAutoSave && shouldUpdateState) {
        handleAutoSave(attribute.id, newValue);
      }
    },
    [attribute, handleAutoSave, handleSave]
  );

  /**
   * Handles modal cancellation by identifying and deleting any newly uploaded documents
   * and subsequently reverting form state to its initial value.
   * @function
   * @returns {void}
   */
  const handleCancelChanges = useCallback(() => {
    exitFullScreen();
    if (attribute?.data_type === 'Document') {
      // Stop any in-progress uploads for this attribute
      activeBatches.forEach((batch) => {
        (batch.data || []).forEach((f) => {
          if (f.attribute_id === attribute?.id && f.key) {
            handleStopUpload(f.key);
          }
        });
      });

      const initialIDs = new Set(
        (Array.isArray(initialValueRef.current) ? initialValueRef.current : [])
          .map((doc) => doc?.document_id)
          .filter(Boolean)
      );
      const currentIDs = (Array.isArray(value) ? value : [])
        .map((doc) => doc?.document_id)
        .filter(Boolean);

      currentIDs.forEach((id) => {
        if (!initialIDs.has(id)) {
          deleteDocument({ document_id: id });
        }
      });
    }
    handleValueChange(initialValueRef.current);
    handleClose();
  }, [
    exitFullScreen,
    handleClose,
    handleValueChange,
    attribute,
    value,
    deleteDocument,
    handleStopUpload,
    activeBatches,
  ]);

  /**
   * Below handleOnClose specially added for document type attr as we need to save tags
   * for that doc once user click on save button.
   * If attr is other than doc type just call handleClose.
   */

  const handleOnClose = useCallback(() => {
    // DocumentFieldComponent now handles completeUpload directly.
    // Save button simply closes the modal.
    handleClose();
  }, [handleClose]);
  // store the initial value when this model get opened
  useEffect(() => {
    // if not open don't store value inside ref
    if (!open) return;

    // take snapshot of old value only if not already taken
    initialValueRef.current = value;
  }, [open]);

  /**
   * Get modal size based on data type and input type
   * @returns {string} The modal size
   */
  const getModalSize = useCallback(() => {
    const dataType = attribute?.data_type;
    const inputType = attribute?.input_control_type;

    switch (inputType) {
      case 'TEXTFIELD_EXTENDED':
        return 'lg';
      default:
        break;
    }

    switch (dataType) {
      case 'Table':
      case 'AdvancedTable':
        return 'xl';
      case 'Chart':
        return 'md';
      case 'Checklist':
        return 'sm';
      case 'Document':
        return 'sm';
      case 'Paragraph':
        return 'lg';
      case 'FieldChange':
        return 'xl';
      default:
        return 'sm';
    }
  }, [attribute?.data_type, attribute?.input_control_type]);

  /**
   * Renders the appropriate field component based on attribute data type
   * @function
   * @returns {React.ReactElement} The rendered field component
   */
  const renderFieldComponent = useCallback(() => {
    if (!attribute) return null;

    const isLongLabel = attribute?.label?.length > 90;

    const fieldProps = {
      label: isLongLabel
        ? attribute?.label?.slice(0, 90) + '...'
        : attribute?.label,
      titleLabel: attribute?.label,
      value,
      onChange: handleValueChange,
      attribute,
      entityDetailsFormikForm,
      required: isRequired, // Add required prop
      configColumn,
      modelTemplateDetails,
    };

    const dataType = attribute?.data_type;
    const inputType = attribute?.input_control_type;

    switch (inputType) {
      case 'TEXTFIELD_EXTENDED':
        return <TextFieldExtendedComponent {...fieldProps} />;
      case 'IFRAME':
        return <IframeFieldComponent {...fieldProps} />;
      default:
        break;
    }

    switch (dataType) {
      case 'Table':
        return <TableFieldComponent {...fieldProps} />;

      case 'AdvancedTable':
        return (
          <AdvancedTableRenderer
            {...fieldProps}
            entityData={entityData}
            usersList={usersList}
            groupList={groupList}
            isViewMode={false}
          />
        );

      case 'Chart':
        return <IframeFieldComponent {...fieldProps} />;

      case 'Checklist':
        return (
          <ChecklistFieldComponent
            {...fieldProps}
            entityId={entityData?.entity_id}
            entityType={entityData?.entity_type}
          />
        );

      case 'Document':
        return (
          <DocumentFieldComponent {...fieldProps} entityData={entityData} />
        );
      case 'Paragraph':
        return <ParagraphFieldRenderer {...fieldProps} />;
      default:
        return null;
    }
  }, [attribute, handleValueChange, entityData, entityDetailsFormikForm]);

  const selectedFilterName =
    value?.type === 'savedSearch' ? value?.filterName?.trim?.() : '';

  /**
   * Get modal title based on data type and input type
   * @returns {string} The modal title
   */
  const getModalTitle = useCallback(() => {
    const dataType = attribute?.data_type;
    const inputType = attribute?.input_control_type;
    const label = attribute?.label || attribute?.name;

    switch (inputType) {
      case 'TEXTFIELD_EXTENDED':
        return isDerivedExtended ? `View` : `Edit`;

      default:
        break;
    }

    switch (dataType) {
      case 'Table':
        return `Edit Table - ${label}`;
      case 'AdvancedTable':
        return `Edit Advanced Table - ${label}`;
      case 'Chart':
        return `Edit Chart - ${label}`;
      case 'Checklist':
        return selectedFilterName || `Edit Checklist - ${label}`;
      case 'Document':
        return `Edit Document - ${label}`;
      case 'Paragraph':
        return `Edit Paragraph - ${label}`;
      default:
        return `Edit ${label}`;
    }
  }, [
    attribute?.data_type,
    attribute?.input_control_type,
    attribute?.label,
    attribute?.name,
  ]);

  return (
    <Dialog
      onClose={handleCancelChanges}
      open={open}
      fullScreen={isFullScreen}
      maxWidth={isFullScreen ? false : getModalSize()}
      fullWidth
      aria-labelledby="multi-edit-attribute-modal"
      PaperProps={{
        sx: {
          maxHeight: isFullScreen ? '100vh' : '90vh',
          height: isFullScreen ? '100vh' : 'auto',
        },
      }}
    >
      <DialogTitle>
        <Box component="span" aria-live="polite" aria-atomic="true">
          {getModalTitle()}
        </Box>
      </DialogTitle>

      <Tooltip title={isFullScreen ? 'Exit Full Screen' : 'Full Screen'}>
        <IconButton
          color="primary"
          aria-label={isFullScreen ? 'Exit full screen' : 'Enter full screen'}
          onClick={toggleFullScreen}
          sx={{
            position: 'absolute',
            right: 56,
            top: 8,
          }}
        >
          {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </IconButton>
      </Tooltip>

      <IconButton
        color="primary"
        aria-label="Close dialog"
        onClick={handleCancelChanges}
        sx={{
          position: 'absolute',
          right: 16,
          top: 8,
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent dividers>
        <Grid container spacing={2} sx={{ flexGrow: 1 }}>
          <Grid item xs={12}>
            {renderFieldComponent()}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancelChanges} variant="outlined" title="Cancel">
          Cancel
        </Button>

        <Button
          /**
           * as we are directly manipulating values inside formik form, so we no longer need this save button
           * this button will just close the model
           * Here we will only update tags of doc if the type of attr is Document.
           */
          onClick={handleOnClose}
          variant="contained"
          title={
            isDerivedExtended
              ? 'This action is not supported for derived attributes.'
              : 'Save'
          }
          disabled={isDerivedExtended}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

MultiEditModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
    data_type: PropTypes.string,
    input_control_type: PropTypes.string,
    label: PropTypes.string,
  }).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.array,
    PropTypes.object,
  ]),
  handleSave: PropTypes.func.isRequired,
  usersList: PropTypes.arrayOf(PropTypes.shape({})),
  groupList: PropTypes.arrayOf(PropTypes.shape({})),
  teamUsersList: PropTypes.arrayOf(PropTypes.shape({})),
  entityData: PropTypes.shape({
    entity_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    entity_type: PropTypes.string,
  }),
  entityDetailsFormikForm: PropTypes.shape({
    getFieldProps: PropTypes.func.isRequired,
    errors: PropTypes.objectOf(PropTypes.string).isRequired,
    touched: PropTypes.objectOf(PropTypes.bool).isRequired,
  }).isRequired,
  handleAutoSave: PropTypes.func,
  isDerivedExtended: PropTypes.bool.isRequired,
  configColumn: PropTypes.bool,
  modelTemplateDetails: PropTypes.shape({}),
};

export default MultiEditModal;
